import {
  FIELD_CONFIG,
  type LayoutField,
} from "@/components/features/matter/file-repo/signature-requests/SignatureRequestPdfLayoutEditor.constants";
import { usePdfEditorDocumentUrl } from "@/components/features/matter/file-repo/pdfEditorDocumentUrl";
import { DemoSignatureFieldEditorDialog } from "@/components/features/signature-request/DemoSignatureFieldEditorDialog";
import { getReusableSignerFieldValue } from "@/components/features/signature-request/reusableSignerFieldValue";
import {
  generateSignedSignatureRequestPdfBlob,
  type SignedSignatureRequestField,
} from "@/components/features/signature-request/signatureRequestPdfSigning";
import { AppModal, Button, LoadingSpinner, Typography } from "@/components/ui";
import { PDFJsViewer } from "@/components/ui/PDFJsViewer/PDFJsViewer";
import { cn } from "@/lib/utils/cn";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  CalendarDays,
  CheckSquare2,
  List,
  PenLine,
  Square,
  Type,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { DemoSignedField } from "./DemoSignatureFieldEditorDialog";
import { useSignerTemplateFields } from "./useSignerTemplateFields";
import { SignerFieldText } from "./signerFieldTextStyle";

type PortalFieldValues = Record<string, string>;

type LiveSignatureRequestFieldsViewProps = {
  fields: LayoutField[];
  fileType:
    | "matter-signature-request-file"
    | "firm-signature-request-file"
    | "firm-file";
  hideSignerFields?: boolean;
  isSignerFieldsDisabled?: boolean;
  onSignedDocumentGeneratorChange?: (
    generator: (() => Promise<Blob>) | null,
  ) => void;
  onCompletionChange?: (complete: boolean) => void;
  presignedUrl?: string;
  presignedUrlKey: string;
};

function clonePortalFields(fields: LayoutField[]) {
  return fields.map((field) => ({ ...field })) as DemoSignedField[];
}

function getFieldDisplayLabel(field: LayoutField) {
  const label = field.label.trim();
  return label || FIELD_CONFIG[field.type].label;
}

function isSignatureField(field: LayoutField) {
  return field.type === "signature" || field.type === "initials";
}

function isCheckboxField(field: LayoutField) {
  return field.type === "checkbox";
}

function getFieldDisplayValue(
  field: DemoSignedField,
  fieldValues: PortalFieldValues,
) {
  if (isSignatureField(field) || isCheckboxField(field)) return "";
  return fieldValues[field.id].trim();
}

function getReusableSignatureDataUrl(
  fields: DemoSignedField[],
  signerId: string,
  fieldType: "signature" | "initials",
) {
  return fields.find(
    (field) =>
      field.type === fieldType &&
      field.signerId === signerId &&
      Boolean(field.imageDataUrl),
  )?.imageDataUrl;
}

function isFieldComplete(
  field: DemoSignedField,
  fieldValues: PortalFieldValues,
) {
  if (isSignatureField(field)) {
    return Boolean(field.imageDataUrl);
  }

  if (isCheckboxField(field)) {
    return Boolean(field.checked);
  }

  return Boolean(fieldValues[field.id].trim());
}

function toggleCheckboxField(fields: DemoSignedField[], fieldId: string) {
  return fields.map((field) =>
    field.id === fieldId ? { ...field, checked: !field.checked } : field,
  );
}

function sortFields(fields: DemoSignedField[]) {
  return [...fields].sort((left, right) => {
    const leftOrder = left.signerOrder ?? Number.POSITIVE_INFINITY;
    const rightOrder = right.signerOrder ?? Number.POSITIVE_INFINITY;
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }
    if (left.page !== right.page) {
      return left.page - right.page;
    }
    if (left.y !== right.y) {
      return left.y - right.y;
    }
    if (left.x !== right.x) {
      return left.x - right.x;
    }
    return left.label.localeCompare(right.label);
  });
}

export function LiveSignatureRequestFieldsView({
  fields,
  fileType,
  hideSignerFields = false,
  isSignerFieldsDisabled = false,
  onSignedDocumentGeneratorChange,
  onCompletionChange,
  presignedUrl,
  presignedUrlKey,
}: LiveSignatureRequestFieldsViewProps) {
  const portalFields = useMemo(() => clonePortalFields(fields), [fields]);
  const [fieldState, setFieldState] = useState<DemoSignedField[]>(
    sortFields(portalFields),
  );
  const [fieldValues, setFieldValues] = useState<PortalFieldValues>(() =>
    portalFields.reduce<PortalFieldValues>((acc, field) => {
      if (!isSignatureField(field) && !isCheckboxField(field)) {
        acc[field.id] = "";
      }
      return acc;
    }, {}),
  );
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [isLegendModalOpen, setIsLegendModalOpen] = useState(false);
  const [scrollToPage, setScrollToPage] = useState<
    ((pageNumber: number) => void) | null
  >(null);
  const isDesktop = useMediaQuery("(min-width: 900px)");
  const { isPreparingDocumentUrl, resolvedDocUrl } = usePdfEditorDocumentUrl({
    fileType,
    initialDocUrl: presignedUrl,
    presignedUrlKey,
    unauthenticated: true,
  });

  useEffect(() => {
    setFieldState(sortFields(portalFields));
    setFieldValues(
      portalFields.reduce<PortalFieldValues>((acc, field) => {
        if (!isSignatureField(field) && !isCheckboxField(field)) {
          acc[field.id] = "";
        }
        return acc;
      }, {}),
    );
    setActiveFieldId(null);
  }, [portalFields]);

  useSignerTemplateFields(resolvedDocUrl, setFieldState);

  const currentSignerOrder = useMemo(() => {
    const incompleteOrders = sortFields(fieldState)
      .filter((field) => field.signerOrder != null)
      .filter((field) => !isFieldComplete(field, fieldValues))
      .map((field) => field.signerOrder ?? Number.POSITIVE_INFINITY)
      .filter((order) => Number.isFinite(order))
      .sort((left, right) => left - right);

    return incompleteOrders.length > 0 ? incompleteOrders[0] : null;
  }, [fieldState, fieldValues]);

  const fieldIsLocked = useCallback(
    (field: DemoSignedField) =>
      currentSignerOrder != null &&
      field.signerOrder != null &&
      field.signerOrder > currentSignerOrder,
    [currentSignerOrder],
  );

  useEffect(() => {
    if (!resolvedDocUrl) {
      onSignedDocumentGeneratorChange?.(null);
      return;
    }

    onSignedDocumentGeneratorChange?.(async () => {
      if (!resolvedDocUrl) {
        throw new Error("Unable to prepare the signature request document.");
      }

      return generateSignedSignatureRequestPdfBlob({
        fieldValues,
        fields: sortFields(fieldState) as SignedSignatureRequestField[],
        sourceUrl: resolvedDocUrl,
      });
    });

    return () => {
      onSignedDocumentGeneratorChange?.(null);
    };
  }, [
    fieldState,
    fieldValues,
    onSignedDocumentGeneratorChange,
    resolvedDocUrl,
  ]);

  const activeSignerFieldsComplete = useMemo(() => {
    const activeFields = sortFields(fieldState).filter((field) => {
      if (currentSignerOrder == null) {
        return true;
      }
      return (
        field.signerOrder == null || field.signerOrder === currentSignerOrder
      );
    });

    return activeFields.every((field) => isFieldComplete(field, fieldValues));
  }, [currentSignerOrder, fieldState, fieldValues]);

  useEffect(() => {
    onCompletionChange?.(activeSignerFieldsComplete);
  }, [activeSignerFieldsComplete, onCompletionChange]);

  const legendFields = useMemo(() => sortFields(fieldState), [fieldState]);

  const jumpToPage = useCallback(
    (pageNumber: number) => {
      scrollToPage?.(pageNumber);
    },
    [scrollToPage],
  );

  const handleLegendFieldClick = useCallback(
    (field: DemoSignedField) => {
      setIsLegendModalOpen(false);
      window.requestAnimationFrame(() => {
        jumpToPage(field.page);
      });
    },
    [jumpToPage],
  );

  if (isPreparingDocumentUrl || !resolvedDocUrl) {
    return (
      <div className="flex h-full items-center justify-center gap-3">
        <LoadingSpinner />
        <Typography className="text-muted-foreground text-sm">
          Preparing PDF…
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 overflow-hidden">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {!isDesktop ? (
          <div className="dark:bg-card/95 sticky top-0 z-20 flex justify-end border-b bg-white/95 px-3 py-2 backdrop-blur lg:hidden">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setIsLegendModalOpen(true);
              }}
            >
              <List className="mr-2 h-4 w-4" />
              Signer fields
            </Button>
          </div>
        ) : null}
        <div className="min-h-0 flex-1 overflow-hidden">
          <PDFJsViewer
            src={resolvedDocUrl}
            showToolbar
            defaultZoomMultiplier={0.9}
            className="h-full"
            contentClassName="p-4"
            onReady={({ scrollToPage: nextScrollToPage }) => {
              setScrollToPage(() => nextScrollToPage);
            }}
            renderPageOverlay={({ pageNumber, pageWidth, scale }) => (
              <div className="absolute inset-0">
                {sortFields(fieldState)
                  .filter((field) => field.page === pageNumber)
                  .map((field) => {
                    if (hideSignerFields) {
                      return null;
                    }

                    const reusableSignatureDataUrl =
                      getReusableSignatureDataUrl(
                        fieldState,
                        field.signerId,
                        field.type === "signature" ? "signature" : "initials",
                      );
                    const isComplete = isFieldComplete(field, fieldValues);
                    const displayValue = getFieldDisplayValue(
                      field,
                      fieldValues,
                    );
                    const isLocked =
                      fieldIsLocked(field) || isSignerFieldsDisabled;

                    return (
                      <button
                        key={field.id}
                        type="button"
                        className={cn(
                          "absolute overflow-hidden rounded-[4px] border px-2 py-1 text-left shadow-sm transition-colors",
                          "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
                          isLocked &&
                            "border-slate-300 bg-slate-100/90 text-slate-500",
                          isComplete
                            ? "border-emerald-500/80 bg-transparent hover:bg-transparent"
                            : field.type === "checkbox"
                              ? "border-emerald-500/80 bg-emerald-500/10 hover:bg-emerald-500/15"
                              : "border-blue-500/80 bg-blue-500/10 hover:bg-blue-500/15",
                          isLocked ? "cursor-not-allowed opacity-40" : "",
                        )}
                        disabled={isLocked}
                        style={{
                          left: `${field.x * 100}%`,
                          top: `${field.y * 100}%`,
                          width: `${field.width * 100}%`,
                          height: `${field.height * 100}%`,
                        }}
                        onClick={() => {
                          if (isLocked) {
                            return;
                          }

                          if (
                            field.type === "checkbox" &&
                            !field.imageDataUrl
                          ) {
                            setFieldState((current) =>
                              toggleCheckboxField(current, field.id),
                            );
                            return;
                          }

                          if (
                            (field.type === "initials" ||
                              field.type === "signature") &&
                            !field.imageDataUrl &&
                            reusableSignatureDataUrl
                          ) {
                            setFieldState((current) =>
                              current.map((item) =>
                                item.id === field.id
                                  ? {
                                      ...item,
                                      imageDataUrl: reusableSignatureDataUrl,
                                    }
                                  : item,
                              ),
                            );
                            return;
                          }

                          if (!isComplete) {
                            const reusableValue = getReusableSignerFieldValue(
                              fieldState,
                              fieldValues,
                              field,
                            );
                            if (reusableValue) {
                              setFieldValues((current) => ({
                                ...current,
                                [field.id]: reusableValue,
                              }));
                              return;
                            }
                          }

                          setActiveFieldId(field.id);
                        }}
                      >
                        {field.imageDataUrl ? (
                          <img
                            src={field.imageDataUrl}
                            alt=""
                            className="h-full w-full object-fill"
                            draggable={false}
                          />
                        ) : field.type === "checkbox" ? (
                          <div className="flex h-full w-full items-center justify-center">
                            {field.checked ? (
                              <CheckSquare2
                                className={cn(
                                  "h-4 w-4",
                                  isLocked
                                    ? "text-slate-400"
                                    : "text-emerald-600",
                                )}
                              />
                            ) : (
                              <Square
                                className={cn(
                                  "h-4 w-4",
                                  isLocked
                                    ? "text-slate-400"
                                    : "text-emerald-600",
                                )}
                              />
                            )}
                          </div>
                        ) : displayValue ? (
                          <SignerFieldText
                            field={field}
                            value={displayValue}
                            scale={scale}
                            pageWidth={pageWidth}
                          />
                        ) : (
                          <div
                            className={cn(
                              "flex h-full w-full items-center justify-center gap-1.5 text-[11px] font-semibold",
                              isLocked
                                ? "text-slate-500"
                                : isComplete
                                  ? "text-emerald-700"
                                  : "",
                            )}
                          >
                            {field.type === "date" ? (
                              <CalendarDays className="h-3.5 w-3.5" />
                            ) : field.type === "text" ? (
                              <Type className="h-3.5 w-3.5" />
                            ) : (
                              <PenLine className="h-3.5 w-3.5" />
                            )}
                            {displayValue || getFieldDisplayLabel(field)}
                          </div>
                        )}
                      </button>
                    );
                  })}
              </div>
            )}
          />
        </div>
      </div>

      {!hideSignerFields && isDesktop ? (
        <aside className="sticky top-0 flex h-full min-h-0 w-72 flex-col border-l bg-slate-50/80 backdrop-blur">
          <div className="border-b px-3 py-2">
            <Typography className="text-sm font-medium">
              Signer fields
            </Typography>
            <Typography className="text-muted-foreground text-xs">
              {legendFields.length} field{legendFields.length === 1 ? "" : "s"}
            </Typography>
            {currentSignerOrder != null ? (
              <Typography className="text-muted-foreground mt-1 text-[11px]">
                Pending signer order {currentSignerOrder}
              </Typography>
            ) : null}
          </div>

          <div className="min-h-0 flex-1 overflow-auto p-3">
            <div className="space-y-1">
              {legendFields.map((field) => {
                const displayValue = getFieldDisplayValue(field, fieldValues);

                return (
                  <button
                    key={field.id}
                    type="button"
                    className={cn(
                      "bg-card flex w-full items-start justify-between gap-2 rounded-md border px-2.5 py-2 text-left text-[11px] shadow-sm transition-colors",
                      isFieldComplete(field, fieldValues)
                        ? "border-emerald-500/70 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/30"
                        : "text-foreground dark:border-border dark:hover:bg-muted border-slate-200 hover:border-blue-300 hover:bg-blue-50",
                      fieldIsLocked(field) || isSignerFieldsDisabled
                        ? "cursor-not-allowed opacity-40"
                        : "",
                    )}
                    disabled={fieldIsLocked(field) || isSignerFieldsDisabled}
                    onClick={() => {
                      jumpToPage(field.page);
                    }}
                    title={`Jump to page ${field.page}`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate">
                        {getFieldDisplayLabel(field)}
                      </span>
                      {displayValue ? (
                        <span className="text-muted-foreground mt-0.5 block truncate text-[10px]">
                          {displayValue}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className={cn(
                        "shrink-0",
                        isFieldComplete(field, fieldValues)
                          ? "text-emerald-600"
                          : "text-muted-foreground",
                      )}
                    >
                      Page {field.page}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </aside>
      ) : null}

      {!hideSignerFields && !isDesktop ? (
        <AppModal
          open={isLegendModalOpen}
          onOpenChange={setIsLegendModalOpen}
          title="Signer fields"
          description={`${legendFields.length} field${legendFields.length === 1 ? "" : "s"}`}
          size="md"
          bodyContained
        >
          <div className="space-y-1">
            {legendFields.map((field) => {
              const displayValue = getFieldDisplayValue(field, fieldValues);

              return (
                <Button
                  key={field.id}
                  type="button"
                  variant="outline"
                  className={cn(
                    "flex h-auto w-full items-start justify-between gap-3 px-3 py-2 text-left",
                    isFieldComplete(field, fieldValues)
                      ? "border-emerald-500/70 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/30"
                      : "text-foreground dark:border-border dark:hover:bg-muted border-slate-200 hover:border-blue-300 hover:bg-blue-50",
                    fieldIsLocked(field) || isSignerFieldsDisabled
                      ? "cursor-not-allowed opacity-40"
                      : "",
                  )}
                  disabled={fieldIsLocked(field) || isSignerFieldsDisabled}
                  onClick={() => {
                    handleLegendFieldClick(field);
                  }}
                >
                  <span className="min-w-0">
                    <span className="block truncate">
                      {getFieldDisplayLabel(field)}
                    </span>
                    {displayValue ? (
                      <span className="text-muted-foreground mt-0.5 block truncate text-xs font-normal">
                        {displayValue}
                      </span>
                    ) : null}
                  </span>
                  <span
                    className={cn(
                      "shrink-0 text-xs",
                      isFieldComplete(field, fieldValues)
                        ? "text-emerald-600"
                        : "text-muted-foreground",
                    )}
                  >
                    Page {field.page}
                  </span>
                </Button>
              );
            })}
          </div>
        </AppModal>
      ) : null}

      {activeFieldId && (
        <DemoSignatureFieldEditorDialog
          activeFieldId={activeFieldId}
          fieldValues={fieldValues}
          fields={fieldState}
          onClose={() => {
            setActiveFieldId(null);
          }}
          setFieldValues={setFieldValues}
          setFields={setFieldState}
        />
      )}
    </div>
  );
}
